/**
 * One shared AudioContext for sound effects and music, created on the first gesture (browsers, iOS Safari
 * in particular, only start audio after a tap or key press).
 */
let ctx: AudioContext | undefined;
let unlocked = false;
/** Why audio is suspended right now (hidden tab, "Sếp tới!", game moved to another tab). */
const suspendedBy = new Set<string>();
const waiting: ((ctx: AudioContext) => void)[] = [];

function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    ctx = new AudioContext();
  } catch {
    ctx = undefined;
    return;
  }
  for (const cb of waiting.splice(0)) cb(ctx);
}

let listening = false;

/** The context once the player has interacted, else undefined. */
export function audioContext(): AudioContext | undefined {
  if (!listening && typeof window !== 'undefined') {
    listening = true;
    window.addEventListener('pointerdown', unlock, { once: true, capture: true });
    window.addEventListener('keydown', unlock, { once: true, capture: true });
  }
  if (ctx?.state === 'suspended' && suspendedBy.size === 0) void ctx.resume();
  return ctx;
}

/** Run `cb` with the context as soon as audio is allowed (immediately if it already is). */
export function onAudioReady(cb: (ctx: AudioContext) => void): void {
  const now = audioContext();
  if (now) cb(now);
  else waiting.push(cb);
}

/**
 * Suspend the whole audio graph (ux-screens §7.2): nothing keeps running on the sound card while the game is
 * hidden or behind "Sếp tới!". Resumes when no reason is left.
 */
export function setAudioSuspended(reason: string, suspended: boolean): void {
  if (suspended) suspendedBy.add(reason);
  else suspendedBy.delete(reason);
  if (!ctx) return;
  if (suspendedBy.size > 0 && ctx.state === 'running') void ctx.suspend();
  else if (suspendedBy.size === 0 && ctx.state === 'suspended') void ctx.resume();
}

/** For tests and debugging: the context's state, or 'none' before the first gesture. */
export function audioState(): AudioContextState | 'none' {
  return ctx?.state ?? 'none';
}

/**
 * Mix with what the phone is already playing (YouTube, Spotify…) instead of stopping it: Audio Session API
 * type 'ambient' (Safari 16.4+, Chromium where available). Silently does nothing elsewhere.
 */
export function preferAmbientAudio(): void {
  if (typeof navigator === 'undefined') return;
  const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
  if (session) session.type = 'ambient';
}
